<template>
    <v-card :loading="loading">
        <v-card-title class="d-flex align-center">
            <v-btn icon class="mr-2" color="primary" @click="close">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <span>{{ title }}</span>
        </v-card-title>
        <v-divider class="mx-4" />
        <v-card-text class="pt-4">
            <p class="mb-4 grey--text text--darken-1">
                Apply the same milestone data to the selected items (<strong
                    >({{ selectedCount }} of {{ items.length }})</strong
                >.
            </p>

            <v-form v-model="valid">
                <v-row no-gutters cols="12">
                    <v-col cols="12" sm="4" class="pr-sm-3">
                        <v-menu
                            v-model="menuDate"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="form.date"
                                    label="Milestone Date*"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    hide-details="auto"
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules="[rules.required]"
                                />
                            </template>
                            <v-date-picker
                                v-model="form.date"
                                @input="menuDate = false"
                            />
                        </v-menu>
                    </v-col>
                    <v-col
                        cols="12"
                        sm="8"
                        class="d-flex align-end mt-6 mt-sm-0"
                    >
                        <v-slider
                            v-model="form.percentage"
                            step="5"
                            show-ticks="always"
                            thick-size="4"
                            :min="0"
                            :max="100"
                            :color="componentColor(form.percentage)"
                            thumb-label="always"
                            class="ma-0 ml-3"
                            hide-details
                            label="Percentage"
                            :thumb-size="28"
                        />
                    </v-col>
                </v-row>

                <v-row class="d-flex flex-wrap mt-5 mx-0 align-center">
                    <h4 class="mt-2 mr-2">Files:</h4>
                    <v-btn
                        v-for="(item, i) in localFiles"
                        :key="i"
                        color="transparent"
                        elevation="0"
                        small
                        class="ma-1 text-capitalize text--darken-2 pa-2"
                    >
                        <span
                            class="text-truncate"
                            :style="{
                                'font-size': '11px',
                                color: '#2b81d6',
                                'text-decoration': 'underline',
                                cursor: 'pointer',
                                'max-width': '200px',
                            }"
                            @click.stop="openLocalFile(item)"
                            >{{ item.file.name }}</span
                        >
                        <v-icon
                            class="ml-1"
                            small
                            @click.stop="removeLocalFile(i)"
                        >
                            mdi-close-circle
                        </v-icon>
                    </v-btn>
                    <v-btn
                        class="mx-2"
                        depressed
                        fab
                        x-small
                        color="primary"
                        :disabled="loading"
                        :style="{ width: '20px', height: '20px' }"
                        @click="onButtonClick"
                    >
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <input
                        ref="milestoneFiles"
                        class="d-none"
                        type="file"
                        @change="onFileChanged"
                    />
                </v-row>

                <v-row no-gutters cols="12" class="mt-4">
                    <v-textarea
                        v-model="form.notes"
                        hide-details="auto"
                        prepend-icon="mdi-text"
                        label="Notes *"
                        :rules="[rules.required]"
                        required
                        rows="2"
                    />
                </v-row>
            </v-form>

            <div class="d-flex align-center mt-5 mb-2">
                <span class="subtitle-2">Items</span>
                <v-spacer />
                <v-btn
                    text
                    x-small
                    color="primary"
                    :disabled="loading || items.length === 0"
                    @click="toggleAll(true)"
                >
                    Select all
                </v-btn>
                <v-btn
                    text
                    x-small
                    color="primary"
                    :disabled="loading || items.length === 0"
                    @click="toggleAll(false)"
                >
                    Clear
                </v-btn>
            </div>

            <div class="bulk-items-panel">
                <div
                    v-for="item in items"
                    :key="item.key"
                    class="bulk-items-panel__row"
                    @click="item.selected = !item.selected"
                >
                    <v-checkbox
                        v-model="item.selected"
                        dense
                        hide-details
                        class="mt-0 pt-0 shrink"
                        @click.stop
                    />
                    <div class="bulk-items-panel__main">
                        <span class="bulk-items-panel__quote">{{
                            item.quoteNumber
                        }}</span>
                        <span class="bulk-items-panel__name">{{
                            item.reference
                        }}</span>
                    </div>
                </div>
                <div
                    v-if="items.length === 0"
                    class="grey--text text-center py-6"
                >
                    No items in this scope
                </div>
            </div>

            <p v-if="loading && progressLabel" class="mt-4 mb-0 caption">
                {{ progressLabel }}
            </p>
        </v-card-text>
        <v-divider class="mx-4" />
        <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn text color="primary" :disabled="loading" @click="close">
                Cancel
            </v-btn>
            <v-btn
                color="primary"
                :loading="loading"
                :disabled="!canSave"
                @click="save"
            >
                Save
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import moment from 'moment'
import API from '@/services/api'
import { saveFile } from '@/services/storage/saveFile.js'

export default {
    name: 'BulkMilestoneDialog',
    props: {
        group: {
            type: Object,
            default: () => null,
        },
        milestoneId: {
            type: String,
            default: () => '',
            required: true,
        },
        milestones: {
            type: Array,
            default: () => [],
        },
        members: {
            type: Array,
            default: () => [],
        },
        settingId: {
            type: String,
            default: () => '',
            required: true,
        },
    },
    data: () => ({
        loading: false,
        valid: false,
        menuDate: false,
        progressLabel: '',
        companyId: JSON.parse(localStorage.getItem('company')),
        localFiles: [],
        items: [],
        form: {
            date: moment().format('YYYY-MM-DD'),
            percentage: 0,
            notes: '',
        },
        rules: {
            required: v => !!v || 'The value is required',
        },
    }),
    computed: {
        milestoneName() {
            const found = (this.milestones || []).find(
                m => m.id == this.milestoneId
            )
            return found ? found.name : 'Milestone'
        },
        title() {
            const groupName = (this.group && this.group.name) || 'Scope'
            return `${groupName} - ${this.milestoneName} (bulk)`
        },
        selectedItems() {
            return this.items.filter(item => item.selected)
        },
        selectedCount() {
            return this.selectedItems.length
        },
        canSave() {
            return (
                this.valid &&
                !!this.form.date &&
                !!(this.form.notes && String(this.form.notes).trim()) &&
                this.selectedCount > 0 &&
                !this.loading
            )
        },
    },
    created() {
        this.initItems()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        initItems() {
            this.items = (this.members || []).map(member => ({
                key: `${member.quoteId}/${member.id}`,
                costId: member.id,
                quoteId: member.quoteId,
                quoteNumber: member.quoteNumber || '',
                reference: member.reference || '',
                existingFiles:
                    (member.formatMilestones &&
                        member.formatMilestones[this.milestoneId] &&
                        member.formatMilestones[this.milestoneId].files) ||
                    [],
                selected: true,
            }))
        },
        toggleAll(value) {
            this.items.forEach(item => {
                item.selected = value
            })
        },
        componentColor(value) {
            if (!value || value == 0) return 'error'
            if (value < 100) return 'primary'
            return 'success'
        },
        onButtonClick() {
            this.$refs.milestoneFiles.click()
        },
        async onFileChanged(e) {
            try {
                if (!(e.target.files && e.target.files.length > 0)) {
                    return
                }
                let file = e.target.files[0]
                const fileName =
                    moment().format('YYYY-MM-DD HH:mm ') + file.name
                file = new File([file], fileName, {
                    type: file.type,
                })
                this.localFiles = [
                    ...this.localFiles,
                    {
                        url: URL.createObjectURL(file),
                        file,
                    },
                ]
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                if (e.target) {
                    e.target.value = ''
                }
            }
        },
        removeLocalFile(index) {
            const [removed] = this.localFiles.splice(index, 1)
            if (removed && removed.url) {
                URL.revokeObjectURL(removed.url)
            }
        },
        openLocalFile(item) {
            if (item && item.url) {
                window.open(item.url, '_blank')
            }
        },
        async uploadFilesForCost(item) {
            for (const localFile of this.localFiles) {
                await saveFile(
                    localFile.file,
                    `${this.companyId}/quotes/${item.quoteId}/costs/${item.costId}`
                )
            }
        },
        close() {
            this.$emit('close')
        },
        async save() {
            if (!this.canSave) {
                return
            }
            try {
                this.loading = true
                const results = []
                const selected = this.selectedItems
                const total = selected.length
                const newFileNames = this.localFiles.map(
                    localFile => localFile.file.name
                )
                const notes = String(this.form.notes).trim()
                const percentage = Number(this.form.percentage || 0)

                for (let i = 0; i < total; i++) {
                    const item = selected[i]
                    this.progressLabel = `Saving ${i + 1}/${total}…`

                    if (this.localFiles.length > 0) {
                        await this.uploadFilesForCost(item)
                    }

                    const files = [
                        ...((item.existingFiles && [...item.existingFiles]) ||
                            []),
                        ...newFileNames,
                    ]

                    const payload = {
                        quoteId: item.quoteId,
                        costId: item.costId,
                        milestoneId: this.milestoneId,
                        settingId: this.settingId,
                        date: this.form.date,
                        percentage,
                        notes,
                    }
                    if (newFileNames.length > 0) {
                        payload.files = files
                    }

                    const milestones = await API.updateCostMilestone(payload)
                    results.push({
                        costId: item.costId,
                        milestones,
                    })
                }

                this.$emit('applied', results)
                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.progressLabel = ''
            }
        },
    },
}
</script>

<style scoped>
.bulk-items-panel {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
}
.bulk-items-panel__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid #eeeeee;
    cursor: pointer;
}
.bulk-items-panel__row:hover {
    background: #f5f5f5;
}
.bulk-items-panel__row:last-child {
    border-bottom: none;
}
.bulk-items-panel__main {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}
.bulk-items-panel__quote {
    color: #9e9e9e;
    font-size: 13px;
    font-weight: 500;
}
.bulk-items-panel__name {
    color: rgba(0, 0, 0, 0.87);
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
}
</style>
